/**
 * Normalizes a document from old format to new ingestionState format
 * If document already has ingestionState, returns as-is
 * Otherwise converts deprecated rollforward fields to ingestionState structure
 *
 * @param {Object} doc - Document object (may be in old or new format)
 * @returns {Object} - Document with normalized ingestionState
 */
export const normalizeDocument = (doc) => {
  if (!doc) return doc;

  // If already in new format, return as-is
  if (doc.ingestionState) {
    return doc;
  }

  // Check if this is a rollforward document (old format)
  const isRollForward = doc.isRollForward === true;
  const hasRollForwardFields =
    doc.rollForwardStatus ||
    doc.rollForwardSource ||
    doc.previousDocumentId ||
    doc.rollForwardSourceDocumentKey ||
    doc.rollForwardError ||
    doc.rollForwardShowWalkThrough !== undefined ||
    doc.pdfS3Key ||
    doc.previousPresentationFormat;

  // If no rollforward indicators, return as-is (no ingestionState needed)
  if (!isRollForward && !hasRollForwardFields) {
    return doc;
  }

  // Build ingestionState from old fields
  const ingestionState = {};

  if (isRollForward || hasRollForwardFields) {
    ingestionState.type = 'rollforward';
    ingestionState.status = doc.rollForwardStatus;
    ingestionState.source = doc.rollForwardSource;
    delete doc.isRollForward;
    delete doc.rollForwardStatus;

    if (doc.previousDocumentId) {
      ingestionState.previousDocumentId = doc.previousDocumentId;
      delete doc.previousDocumentId;
    }

    if (doc.previousPresentationFormat) {
      ingestionState.previousPresentationFormat =
        doc.previousPresentationFormat;
      delete doc.previousPresentationFormat;
    }

    if (doc.rollForwardSourceDocumentKey) {
      ingestionState.sourceDocumentKey = doc.rollForwardSourceDocumentKey;
      delete doc.rollForwardSourceDocumentKey;
    }

    if (doc.pdfS3Key) {
      ingestionState.pdfS3Key = doc.pdfS3Key;
      delete doc.pdfS3Key;
    }

    if (doc.rollForwardStart) {
      ingestionState.startTime = doc.rollForwardStart;
      delete doc.rollForwardStart;
    }

    if (doc.rollForwardEnd) {
      ingestionState.endTime = doc.rollForwardEnd;
      delete doc.rollForwardEnd;
    }

    if (doc.rollForwardError) {
      ingestionState.error = doc.rollForwardError;
      delete doc.rollForwardError;
    }

    if (doc.rollForwardShowWalkThrough !== undefined) {
      ingestionState.showWalkThrough = doc.rollForwardShowWalkThrough;
      delete doc.rollForwardShowWalkThrough;
    } else {
      ingestionState.showWalkThrough = false; // default
    }

    if (doc.skipWorkbookGeneration !== undefined) {
      ingestionState.skipWorkbookGeneration = doc.skipWorkbookGeneration;
      delete doc.skipWorkbookGeneration;
    }
  }

  // Return document with ingestionState, removing deprecated fields
  const normalizedDoc = {
    ...doc,
    ingestionState,
  };

  return normalizedDoc;
};

/**
 * Normalizes an array of documents
 *
 * @param {Array} docs - Array of document objects
 * @returns {Array} - Array of normalized documents
 */
export const normalizeDocumentArray = (docs) => {
  if (!Array.isArray(docs)) {
    return docs;
  }
  return docs.map(normalizeDocument);
};
